'use client'

import type { ReactNode } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod/v4'

import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@ui/web/components'
import { AvatarSelector } from '@ui/web/lib'
import { childAvatarList } from '@helper/utils/domain'

import { ConsentField } from './consent-field'
import { useEnrollChildren } from './use-enroll-children'

const SingleEnrollSchema = z.object({
  name: z.string().trim().min(1, 'Wajib diisi'),
  nickname: z.string().trim().max(50, 'Maksimal 50 karakter'),
  image: z.string(),
  birthYear: z.number().int().nullable(),
})

/**
 * Birth years offered, youngest first. Mirrors the account-settings sheet so a child edited later
 * sees the same list they were created with.
 */
function getBirthYearOptions(): number[] {
  const currentYear = new Date().getFullYear()
  return Array.from({ length: 11 }, (_, index) => currentYear - 4 - index)
}

export interface SingleFormProps {
  type: 'web' | 'school'
  consented: boolean
  onConsentChange: (consented: boolean) => void
  /** School-only fields (Kelas, Tingkat), rendered under Tahun lahir. */
  renderExtraFields?: (args: { disabled: boolean }) => ReactNode
  /** Values those extra fields collected, passed straight to `signUpChild`. */
  enrollOptions?: { teamId?: string | null; schoolClass?: string | null }
  /** Shown as a secondary footer button when the host supports bulk enrolment. */
  onSwitchToBulk?: () => void
  onClose: () => void
}

export function SingleForm({
  type,
  consented,
  onConsentChange,
  renderExtraFields,
  enrollOptions,
  onSwitchToBulk,
  onClose,
}: SingleFormProps) {
  const { enroll, isReady } = useEnrollChildren()

  const noun = type === 'web' ? 'anak' : 'murid'

  // A family fills in a whole little profile; a school enrols a name and drops it into a class,
  // filling the rest in later from the roster. Avatar, panggilan akrab, and tahun lahir are
  // therefore the family's fields — the school form stays the two questions it always asked.
  const withProfileFields = type === 'web'

  const form = useForm({
    defaultValues: {
      name: '',
      nickname: '',
      image: childAvatarList[Math.floor(Math.random() * childAvatarList.length)]?.src ?? '',
      birthYear: null,
    } as z.input<typeof SingleEnrollSchema>,
    validators: { onSubmit: SingleEnrollSchema },
    onSubmit: async ({ value, formApi }) => {
      const result = await enroll([value.name.trim()], {
        teamId: enrollOptions?.teamId ?? null,
        schoolClass: (enrollOptions?.schoolClass ?? null) as never,
        // Omitted for a school so the hook draws a random avatar per child, as bulk enrolment does.
        image: withProfileFields ? value.image : undefined,
        nickname: withProfileFields && value.nickname.trim() ? value.nickname.trim() : null,
        birthYear: withProfileFields ? value.birthYear : null,
      })

      if (result.failed.length === 0) {
        toast.success(`${type === 'web' ? 'Anak' : 'Murid'} berhasil didaftarkan.`)
        onClose()
        return
      }

      const failure = result.failed[0]
      if (!failure) return

      // `FieldError` reads `field.state.meta.errors` as issue objects, so a bare string here would
      // flatten into a primitive with no `.message` and render nothing.
      formApi.setFieldMeta('name', (prev) => ({
        ...prev,
        errorMap: { onSubmit: [{ message: failure.message }] },
      }))
      toast.error(failure.message)
    },
  })

  const isSubmitting = useStore(form.store, (s) => s.isSubmitting)
  const disabled = isSubmitting || !isReady

  return (
    <>
      <DialogHeader>
        <DialogTitle>Daftarkan {noun}</DialogTitle>
        <DialogDescription>Daftarkan {noun} agar mereka dapat baca dan belajar secara mandiri.</DialogDescription>
      </DialogHeader>

      <form
        className='srd:flex srd:flex-col srd:gap-4'
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        {withProfileFields && (
          <div className='srd:flex srd:w-full srd:items-center srd:justify-center'>
            <form.Field name='image'>
              {(field) => (
                <AvatarSelector
                  handleAvatarSelectAction={(avatar: { src: string }) => field.handleChange(avatar.src)}
                  avatarData={childAvatarList}
                  initialAvatarKey={
                    childAvatarList.find((a: { src: string; key?: string }) => a.src === field.state.value)?.key ??
                    undefined
                  }
                  size={64}
                  disabled={disabled}
                />
              )}
            </form.Field>
          </div>
        )}

        <form.Field name='name'>
          {(field) => (
            <Field>
              <FieldLabel>{withProfileFields ? `Nama ${noun}` : 'Nama'}</FieldLabel>
              <Input
                placeholder={withProfileFields ? `Nama ${noun}` : 'Ketik nama'}
                value={field.state.value}
                disabled={disabled}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
              />
              {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
            </Field>
          )}
        </form.Field>

        {withProfileFields && (
          <form.Field name='nickname'>
            {(field) => (
              <Field>
                <FieldLabel>Panggilan akrab</FieldLabel>
                <Input
                  placeholder='Panggilan akrab'
                  value={field.state.value}
                  disabled={disabled}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
              </Field>
            )}
          </form.Field>
        )}

        {withProfileFields && (
          <form.Field name='birthYear'>
            {(field) => (
              <Field>
                <FieldLabel>Tahun lahir</FieldLabel>
                <Select
                  value={field.state.value === null ? null : String(field.state.value)}
                  disabled={disabled}
                  onValueChange={(next: string | null) => {
                    // Optional, so pressing the year already picked clears it. Base UI fires this on
                    // every item press, including a press on the current value.
                    const current = field.state.value === null ? null : String(field.state.value)
                    field.handleChange(!next || next === current ? null : Number.parseInt(next, 10))
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Pilih tahun lahir'>
                      {(current: string | null) => current ?? 'Pilih tahun lahir'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {getBirthYearOptions().map((year) => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.state.meta.errors.length > 0 && <FieldError errors={field.state.meta.errors} />}
              </Field>
            )}
          </form.Field>
        )}

        {renderExtraFields?.({ disabled })}

        <ConsentField
          id='enroll-child-consent'
          type={type}
          checked={consented}
          onCheckedChange={onConsentChange}
          disabled={disabled}
        />

        <DialogFooter>
          {onSwitchToBulk ? (
            <Button variant='outline' onClick={onSwitchToBulk} disabled={disabled}>
              Daftar banyak
            </Button>
          ) : (
            <Button variant='outline' onClick={onClose} disabled={disabled}>
              Batal
            </Button>
          )}

          <Button type='submit' loading={isSubmitting} disabled={disabled || !consented}>
            Daftarkan
          </Button>
        </DialogFooter>
      </form>
    </>
  )
}
